import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCircleInfo,
  faClockRotateLeft,
  faCommentDots,
  faFileInvoiceDollar,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { customerRequestThreadPath } from '@/routes/customer-portal-routes';
import type {
  CustomerModificationRound,
  CustomerQuotationRevisionSummary,
} from '@/types/customer-modification.types';
import { CustomerStatusBanner } from '@/components/customer-portal/CustomerStatusBanner';
import type { CustomerBannerTone } from '@/components/customer-portal/CustomerStatusBanner';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';

/**
 * D-131 M7 — the customer's view of their modification request (design §9,
 * readiness §14.1): a coarse status strip, the service team's question with a
 * Reply box, "Add to your request" while the request is open, the quotations
 * sent (numbers, references, dates; the current one marked), and the closed
 * history in plain language.
 *
 * Deliberately absent (design §9.3): who inside the service team holds the
 * request, the classification, any feasibility verdict, any internal note —
 * the API never sends them, and this component has no field to show them.
 * Every message here is either the customer's own words ("You") or something
 * the service team chose to say to the customer.
 */

const MAX_TEXT = 4000;

const STATUS: Record<
  CustomerModificationRound['status'],
  { tone: CustomerBannerTone; title: string; message: string }
> = {
  UNDER_REVIEW: {
    tone: 'pending',
    title: "We're reviewing your request",
    message: 'The service team is looking at what you asked for. Nothing is needed from you yet.',
  },
  INFORMATION_NEEDED: {
    tone: 'warning',
    title: 'We need a little more information',
    message: 'The service team has a question for you. Your reply goes straight back to them.',
  },
  QUOTATION_READY: {
    tone: 'warning',
    title: 'Updated quotation ready for your decision',
    message:
      'The service team has sent an updated quotation. Review it below, then approve, reject or request another change.',
  },
  CLOSED: {
    tone: 'info',
    title: 'Your request has been answered',
    message: 'The service team has responded to your request.',
  },
};

function fmtDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** The service team's latest message on the round, when it is asking something. */
function latestTeamMessage(round: CustomerModificationRound): string | null {
  for (let i = round.entries.length - 1; i >= 0; i -= 1) {
    const e = round.entries[i];
    if (e.author === 'SERVICE_TEAM' && e.body) return e.body;
  }
  return null;
}

const TEXTAREA_CLASS =
  'mt-1.5 block min-h-[5rem] w-full resize-y rounded-customer-sm border border-customer-border bg-customer-surface px-3.5 py-2.5 text-sm leading-5 text-customer-text placeholder:text-customer-text-secondary transition-colors hover:border-customer-text/30 focus:border-customer-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring/40';

function MessageForm({
  id,
  label,
  placeholder,
  submitLabel,
  submitting,
  onSubmit,
}: {
  id: string;
  label: string;
  placeholder: string;
  submitLabel: string;
  submitting: boolean;
  onSubmit: (text: string) => void;
}) {
  const [text, setText] = useState('');
  return (
    <form
      noValidate
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (submitting || !text.trim()) return;
        onSubmit(text.trim());
        setText('');
      }}
    >
      <label htmlFor={id} className="block text-sm font-medium text-customer-text">
        {label}
        <span className="ml-0.5 text-customer-danger-text" aria-hidden="true">
          *
        </span>
      </label>
      <textarea
        id={id}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        rows={3}
        maxLength={MAX_TEXT}
        required
        aria-required="true"
        disabled={submitting}
        className={TEXTAREA_CLASS}
      />
      <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <CustomerButton
          type="submit"
          variant="primary"
          icon={faPaperPlane}
          disabled={!text.trim() || submitting}
          loading={submitting}
          loadingLabel="Sending…"
        >
          {submitLabel}
        </CustomerButton>
      </div>
    </form>
  );
}

interface CustomerModificationSectionProps {
  current: CustomerModificationRound | null;
  history: CustomerModificationRound[];
  revisions: CustomerQuotationRevisionSummary[];
  canRespond: boolean;
  submitting: boolean;
  onReply: (roundId: string, message: string) => void;
  onAddToRequest: (roundId: string, message: string) => void;
}

export function CustomerModificationSection({
  current,
  history,
  revisions,
  canRespond,
  submitting,
  onReply,
  onAddToRequest,
}: CustomerModificationSectionProps) {
  const [showHistory, setShowHistory] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const closed = history.filter((r) => r.status === 'CLOSED' && r.id !== current?.id);
  const sent = revisions.filter((r) => r.sentAt !== null);

  if (!current && closed.length === 0 && sent.length <= 1) return null;

  return (
    <div className="space-y-4" data-testid="customer-modification-section">
      {current && (
        <section aria-label="Your modification request" className="space-y-3">
          <CustomerStatusBanner
            tone={STATUS[current.status].tone}
            title={STATUS[current.status].title}
            message={STATUS[current.status].message}
            icon={faCommentDots}
          />

          <div className="rounded-customer border border-customer-border p-3.5">
            <p className="text-xs font-medium text-customer-text-secondary">
              What you asked for · {fmtDateTime(current.openedAt)}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-customer-text">
              {current.requestText}
            </p>
            {current.currentRevisionNumber !== null && (
              <p className="mt-2 text-xs text-customer-text-secondary">
                Quotation {current.currentQuotationReference ?? ''} (version{' '}
                {current.currentRevisionNumber})
              </p>
            )}

            {/* Stage D-vi (D-145 / OD-D1) — the way through to the canonical
                Request Thread, which is where the full conversation and every
                action now live. The round's own id is already on this object, so
                nothing is derived or guessed; if the link is followed to a round
                this contact may not see, the server answers 404 exactly as it
                would for a direct URL. This section keeps all of its existing
                behaviour — it is a summary on the job page, and remains one. */}
            <Link
              to={customerRequestThreadPath(current.id)}
              data-testid="customer-modification-open-thread"
              className="customer-pressable mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-customer-sm text-sm font-medium text-customer-primary-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
            >
              Open this request
              <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" aria-hidden="true" />
            </Link>
          </div>

          {current.status === 'INFORMATION_NEEDED' && (
            <div
              className="rounded-customer border border-customer-warning/40 bg-customer-warning/[0.08] p-3.5"
              data-testid="customer-modification-question"
            >
              <p className="text-xs font-medium text-customer-text-secondary">
                The service team asked
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-customer-text">
                {latestTeamMessage(current) ?? 'Please provide more details about your request.'}
              </p>
              {canRespond ? (
                <MessageForm
                  id="customer-modification-reply"
                  label="Your reply"
                  placeholder="Answer the service team's question…"
                  submitLabel="Send reply"
                  submitting={submitting}
                  onSubmit={(text) => onReply(current.id, text)}
                />
              ) : (
                <p className="mt-2 text-xs text-customer-text-secondary">
                  An account owner or manager can reply to this question.
                </p>
              )}
            </div>
          )}

          {current.entries.length > 1 && (
            <details className="rounded-customer border border-customer-border p-3.5">
              <summary className="cursor-pointer text-sm font-medium text-customer-text">
                Conversation ({current.entries.length})
              </summary>
              <ul className="mt-2 space-y-2">
                {current.entries.map((e) => (
                  <li key={e.id} className="text-sm leading-5 text-customer-text">
                    <span className="text-xs font-medium text-customer-text-secondary">
                      {e.author === 'YOU' ? 'You' : 'Service team'} · {fmtDateTime(e.createdAt)}
                    </span>
                    {e.body && <p className="mt-0.5 whitespace-pre-wrap">{e.body}</p>}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {current.status !== 'CLOSED' && canRespond && current.status !== 'INFORMATION_NEEDED' && (
            <div>
              {showAdd ? (
                <div className="rounded-customer border border-customer-border p-3.5">
                  <MessageForm
                    id="customer-modification-add"
                    label="Add to your request"
                    placeholder="Anything else the service team should know…"
                    submitLabel="Add to request"
                    submitting={submitting}
                    onSubmit={(text) => {
                      onAddToRequest(current.id, text);
                      setShowAdd(false);
                    }}
                  />
                  <div className="mt-1 flex justify-end">
                    <CustomerButton variant="ghost" size="sm" onClick={() => setShowAdd(false)}>
                      Cancel
                    </CustomerButton>
                  </div>
                </div>
              ) : (
                <CustomerButton
                  variant="outline"
                  size="sm"
                  icon={faCommentDots}
                  onClick={() => setShowAdd(true)}
                >
                  Add to your request
                </CustomerButton>
              )}
            </div>
          )}

          {current.status === 'CLOSED' && current.outcomeMessage && (
            <div className="rounded-customer border border-customer-border p-3.5">
              <p className="text-xs font-medium text-customer-text-secondary">
                The service team's response
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-customer-text">
                {current.outcomeMessage}
              </p>
            </div>
          )}
        </section>
      )}

      {sent.length > 1 && (
        <section
          aria-label="Quotations sent"
          className="rounded-customer border border-customer-border p-3.5"
        >
          <p className="flex items-center gap-2 text-xs font-medium text-customer-text-secondary">
            <FontAwesomeIcon icon={faFileInvoiceDollar} className="h-3 w-3" aria-hidden="true" />
            Quotations sent for this job
          </p>
          <ul className="mt-2 space-y-1">
            {[...sent]
              .sort((a, b) => b.revisionNumber - a.revisionNumber)
              .map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center gap-2 text-sm text-customer-text"
                >
                  <span>
                    Version {r.revisionNumber}
                    {r.quotationReference ? ` · ${r.quotationReference}` : ''}
                  </span>
                  <span className="text-xs text-customer-text-secondary">{fmtDate(r.sentAt)}</span>
                  {r.isCurrent ? (
                    <span className="rounded-full bg-customer-primary/10 px-2 py-0.5 text-xs font-medium text-customer-primary-text">
                      Current — download above
                    </span>
                  ) : (
                    <span className="text-xs text-customer-text-secondary">Replaced</span>
                  )}
                </li>
              ))}
          </ul>
        </section>
      )}

      {closed.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            aria-expanded={showHistory}
            className="flex min-h-11 items-center gap-2 text-sm font-medium text-customer-primary-text hover:underline"
          >
            <FontAwesomeIcon icon={faClockRotateLeft} className="h-3.5 w-3.5" aria-hidden="true" />
            {showHistory ? 'Hide earlier requests' : `Earlier requests (${closed.length})`}
          </button>
          {showHistory && (
            <ul className="mt-2 space-y-2" data-testid="customer-modification-history">
              {closed.map((r) => (
                <li
                  key={r.id}
                  className="rounded-customer border border-customer-border p-3.5 text-sm"
                >
                  <p className="text-xs font-medium text-customer-text-secondary">
                    You asked · {fmtDate(r.openedAt)}
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap text-customer-text">{r.requestText}</p>
                  {r.outcomeMessage && (
                    <>
                      <p className="mt-2 text-xs font-medium text-customer-text-secondary">
                        We replied · {fmtDate(r.closedAt)}
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap text-customer-text">
                        {r.outcomeMessage}
                      </p>
                    </>
                  )}
                  {r.currentRevisionNumber !== null && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-customer-text-secondary">
                      <FontAwesomeIcon icon={faCircleInfo} className="h-3 w-3" aria-hidden="true" />
                      Quotation version {r.currentRevisionNumber}
                      {r.currentQuotationReference ? ` · ${r.currentQuotationReference}` : ''}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
